import React, { FunctionComponent, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { parseCookies } from 'nookies'
import {
  Grid,
  InputCluster,
  Link,
  Button,
  Icon,
  Heading,
  RadioGroup,
  Radio,
  Theme,
  Text,
  ButtonKinds,
  AllIconNames,
  Checkbox,
  Input,
  DarkMode,
} from '@afterpaytouch/core'
import { Country } from '@afterpaytouch/portal-api/types'

import {
  createPortalApi,
  resetStore,
  useAppDispatch,
  useCheckoutTheme,
  useCountry,
  usePersistor,
  useCohort,
  useCheckoutDarkMode,
  useCheckoutDarkModePreference,
} from '../../state'
import { SupportedLocale } from '../../model'
import { BASE_PATH, app } from '../../env'

type Layouts = 'left' | 'right' | 'fullWidth'

interface ThemeConfig {
  background: string
  caretButtonKind: ButtonKinds
  layoutButtonKind: ButtonKinds
}

interface Model {
  token: string
  country: Country
  consumerLocale: SupportedLocale
}

interface LayoutButtonProps {
  kind: ButtonKinds
  onClick: () => void
  icon: AllIconNames
}

enum ApplePaylaterMode {
  Ecomm = '',
  InStore = '/in-store',
  WalletProvisioning = '/wallet-provisioning',
}
interface CheckoutOptions {
  oauthRequest: boolean
  applePaylater: boolean
  applePaylaterMode: ApplePaylaterMode
  sessionIdentifier: string
  verifier: string
}

const LayoutButton: FunctionComponent<LayoutButtonProps> = ({ kind, onClick, icon }) => {
  return (
    <Button size='Small' kind={kind} onClick={onClick} aria-label='layout button'>
      <Icon name={icon} size='Small' />
    </Button>
  )
}

const mapColorSchemeToDarkMode: Record<string, DarkMode> = {
  system: DarkMode.System,
  light: DarkMode.Light,
  dark: DarkMode.Dark,
}

const mapPreferenceToColorScheme: Record<'dark' | 'light' | 'system', string> = {
  system: 'system',
  light: 'light',
  dark: 'dark',
}

export const DevTools: FunctionComponent = () => {
  const country = useCountry()
  const router = useRouter()
  const [form, setForm] = useState<Model>({
    token: '002.xyz',
    country: country ?? router?.locale?.split('-')?.[1] ?? Country.AU,
    consumerLocale: (router.locale as SupportedLocale) ?? 'en-AU',
  })
  const dispatch = useAppDispatch()
  const persistor = usePersistor()
  const portalApi = createPortalApi()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [cookies, setCookies] = useState<any>([])
  const [open, setOpen] = useState(false)
  const [theme, setTheme] = useCheckoutTheme()
  const [, setDarkMode] = useCheckoutDarkMode()
  const [darkModePreference, setDarkModePreference] = useState<'dark' | 'light' | 'system'>(useCheckoutDarkModePreference())
  const [options, setOptions] = useState<CheckoutOptions>({
    oauthRequest: false,
    applePaylater: false,
    applePaylaterMode: ApplePaylaterMode.Ecomm,
    sessionIdentifier: '1234',
    verifier: '5555',
  })
  const [componentLayout, setComponentLayout] = useState<Layouts>('fullWidth')
  const cohort = useCohort()

  const publicEnv = ['us-sbox', 'au-sbox', 'eu-sbox', 'au-prod', 'eu-prod', 'us-prod']
  const shouldRenderThemeSelection = !publicEnv.includes(app.APP_ENVIRONMENT)
  const shouldRenderCohort = !publicEnv.includes(app.APP_ENVIRONMENT)

  useEffect(() => {
    portalApi
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .get<{ cookies: any[] }>('/portal/cookies')
      .then((result) => setCookies(result.cookies))
      .catch((err) => console.warn(err))
  }, [portalApi])

  useEffect(() => {
    const interval = setInterval(() => {
      const cookies = parseCookies()
      const cookieValue = cookies['apt-dark-mode'] as 'dark' | 'light' | 'system'
      const currentPreference = ['dark', 'light', 'system'].includes(cookieValue) ? cookieValue : 'system'

      if (currentPreference !== darkModePreference) {
        setDarkModePreference(currentPreference)
      }
    }, 500)

    return () => clearInterval(interval)
  }, [darkModePreference])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.shiftKey && event.key === 'O') {
        event.preventDefault()
        setOpen((prev) => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const handleFormChanged = (e): void => {
    const { value } = e.target
    const newFormModel = {
      ...form,
      [e.target.name]: value,
    }
    setForm(newFormModel)
  }

  const getRestartUrl = (options: CheckoutOptions): string => {
    if (options.applePaylater) {
      const basePath = [BASE_PATH, 'us', 'apple-paylater'].join('/')
      const baseUrl = `${window.location.origin}${basePath}`

      return `${baseUrl}${options.applePaylaterMode}?sessionIdentifier=${options.sessionIdentifier}&verifier=${options.verifier}`
    }

    const basePath = [BASE_PATH, form.country].join('/')
    const baseUrl = `${window.location.origin}${basePath}`

    return `${baseUrl}?token=${form.token}&consumerLocale=${form.consumerLocale}`
  }

  const appendOAuthParams = (url: string): string =>
    `${url}&response_type=code&scope=create_checkout%20read_user_info&client_id=1&state=obtaining_code&redirect_uri=https://www.afterpay-merchant.com/confirm`

  const handleRestart = async (): Promise<void> => {
    if (persistor === null) {
      return
    }
    const restartUrl = options.oauthRequest ? appendOAuthParams(getRestartUrl(options)) : getRestartUrl(options)
    setOpen(false)
    persistor.pause()
    await persistor.flush()
    await persistor.purge()
    dispatch(resetStore())
    setTimeout(() => {
      window.location.href = restartUrl
    })
  }

  const handleToggle = (): void => {
    setOpen(!open)
  }

  const handleThemeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setTheme(e.target.value as Theme)
  }

  const handleColorSchemeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const colorScheme = e.target.value
    const darkModeValue = mapColorSchemeToDarkMode[colorScheme]
    setDarkMode(darkModeValue)

    if (colorScheme === 'system') {
      setDarkModePreference('system')
    } else if (colorScheme === 'light') {
      setDarkModePreference('light')
    } else if (colorScheme === 'dark') {
      setDarkModePreference('dark')
    }
  }

  const handleOAuthRequestToggle = (e): void => {
    const newOptions = {
      ...options,
      oauthRequest: e.target.checked,
    }
    setOptions(newOptions)
  }

  const handleApplePaylaterToggle = (e): void => {
    const newOptions = {
      ...options,
      applePaylater: e.target.checked,
    }
    setOptions(newOptions)
  }

  const handleApplePaylaterChange = (e): void => {
    const newOptions = {
      ...options,
      applePaylaterMode: e.target.value as ApplePaylaterMode,
    }
    setOptions(newOptions)
  }

  const handleApplePaylaterInputChange = (e): void => {
    const newOptions = {
      ...options,
      [e.target.name]: e.target.value,
    }
    setOptions(newOptions)
  }

  const themeConfigs: Record<Theme, ThemeConfig> = {
    [Theme.AP]: {
      background: '#B2FCE4',
      caretButtonKind: 'Primary',
      layoutButtonKind: 'Secondary',
    },
    [Theme.Cash]: {
      background: 'var(--color-semantic-neutral-100)',
      caretButtonKind: 'Primary',
      layoutButtonKind: 'Secondary',
    },
    [Theme.Pegasus]: {
      background: 'var(--color-semantic-neutral-100)',
      caretButtonKind: 'Secondary',
      layoutButtonKind: 'Primary',
    },
    [Theme.Kelpie]: {
      background: 'var(--color-semantic-neutral-100)',
      caretButtonKind: 'Primary',
      layoutButtonKind: 'Secondary',
    },
  } as const

  const themeConfig = themeConfigs[theme]

  const Caret = open ? Icon.CaretDown : Icon.CaretUp
  const baseComponentStyle = {
    position: 'fixed' as React.CSSProperties['position'],
    bottom: 0,
    width: '100%',
    zIndex: 9999999,
    transform: open ? 'translateY(0)' : 'translateY(calc(100% - 48px))',
    transition: 'ease-out 150ms transform',
    display: 'flex',
    flexDirection: 'column' as React.CSSProperties['flexDirection'],
    pointerEvents: 'none' as React.CSSProperties['pointerEvents'],
  } as const
  const componentStyleLeft = {
    ...baseComponentStyle,
    left: 0,
    maxWidth: '400px',
  } as const
  const componentStyleRight = {
    ...baseComponentStyle,
    right: 0,
    maxWidth: '400px',
  } as const
  const componentStyleFullWidth = {
    ...baseComponentStyle,
    left: 0,
    right: 0,
  } as const

  let componentStyle: React.CSSProperties = componentStyleFullWidth
  if (componentLayout === 'left') {
    componentStyle = componentStyleLeft
  } else if (componentLayout === 'right') {
    componentStyle = componentStyleRight
  }

  const componentDetailStyle = {
    background: themeConfig.background,
    padding: '16px',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    maxHeight: 'calc(100vh - 48px)',
    overflowY: 'auto' as React.CSSProperties['overflowY'],
    width: '100%',
    position: 'relative' as React.CSSProperties['position'],
    zIndex: 9999999,
    pointerEvents: 'auto' as React.CSSProperties['pointerEvents'],
  }
  const componentToggleStyle = {
    transform: 'translateY(10px)',
    position: 'relative' as React.CSSProperties['position'],
    zIndex: 9999998,
    alignSelf: componentLayout === 'left' ? 'flex-start' : 'flex-end',
    marginRight: '16px',
    marginLeft: '16px',
    pointerEvents: 'auto' as React.CSSProperties['pointerEvents'],
  }
  const cookiesStyle = {
    maxHeight: 300,
    overflowY: 'scroll' as React.CSSProperties['overflowY'],
  }
  const restartColumnWidth = componentLayout === 'fullWidth' ? 4 : 12
  const restartColumnHalfWidth = componentLayout === 'fullWidth' ? 4 : 6

  return (
    <div style={componentStyle}>
      <div style={componentToggleStyle}>
        <Button
          size='Regular'
          kind={themeConfig.caretButtonKind}
          onClick={handleToggle}
          aria-label='Toggle dev tools'
          testNameSpace='devtools'
          style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
        >
          <Caret size='Small' />
        </Button>
      </div>
      <div style={componentDetailStyle}>
        <Grid justifyContent='start'>
          <Grid.Item>
            <LayoutButton kind={themeConfig.layoutButtonKind} onClick={() => setComponentLayout('left')} icon='ArrowLeft' />
            <LayoutButton kind={themeConfig.layoutButtonKind} onClick={() => setComponentLayout('fullWidth')} icon='ArrowDown' />
            <LayoutButton kind={themeConfig.layoutButtonKind} onClick={() => setComponentLayout('right')} icon='ArrowRight' />
          </Grid.Item>
        </Grid>
        <Grid>
          <InputCluster
            Top={{
              label: 'Token',
              value: form.token,
              onChange: handleFormChanged,
              name: 'token',
            }}
            Left={{
              label: 'Locale',
              format: 'aa{-}aa',
              value: form.consumerLocale,
              onChange: handleFormChanged,
              name: 'consumerLocale',
            }}
            Right={{
              label: 'Country',
              format: 'aa',
              value: form.country,
              onChange: handleFormChanged,
              name: 'country',
            }}
          />
        </Grid>
        <Grid verticalGutter={0.5}>
          {shouldRenderCohort && (
            <Grid.Item sm={12} md={restartColumnWidth}>
              <Heading size='S'>Cohort</Heading>
              <Text>{cohort}</Text>
            </Grid.Item>
          )}
          {shouldRenderThemeSelection && (
            <>
              <Grid.Item sm={6} md={restartColumnHalfWidth}>
                <Heading size='S'>Theme</Heading>
                <RadioGroup id='devtools-theme-radio-group' name='theme' defaultValue={theme} onChange={handleThemeChange}>
                  <Radio.Button value={Theme.AP} label='AP' />
                  <Radio.Button value={Theme.Cash} label='CASH' />
                  <Radio.Button value={Theme.Pegasus} label='PEGASUS' />
                </RadioGroup>
              </Grid.Item>
              <Grid.Item sm={6} md={restartColumnHalfWidth}>
                <Heading size='S'>Mode</Heading>
                <RadioGroup
                  id='devtools-mode-radio-group'
                  name='mode'
                  value={mapPreferenceToColorScheme[darkModePreference]}
                  onChange={handleColorSchemeChange}
                >
                  <Radio.Button value='system' label='System' />
                  <Radio.Button value='light' label='Light' />
                  <Radio.Button value='dark' label='Dark' />
                </RadioGroup>
              </Grid.Item>
            </>
          )}
        </Grid>
        <Grid verticalGutter={0.5}>
          <Grid.Item sm={12}>
            <Checkbox label='OAuth Request?' onChange={handleOAuthRequestToggle} />
            <Checkbox label='Apple Paylater?' onChange={handleApplePaylaterToggle} />
          </Grid.Item>
          {options.applePaylater && (
            <>
              <Grid.Item sm={12}>
                <Heading size='S'>Apple Pay Later Mode</Heading>
                <RadioGroup
                  id='devtools-apple-paylater-radio-group'
                  name='apple-paylater'
                  defaultValue={ApplePaylaterMode.Ecomm}
                  onChange={handleApplePaylaterChange}
                >
                  <Radio.Button value={ApplePaylaterMode.Ecomm} label='Ecomm (Online)' />
                  <Radio.Button value={ApplePaylaterMode.InStore} label='InStore (Returning User)' />
                  <Radio.Button value={ApplePaylaterMode.WalletProvisioning} label='Wallet Provisioning (New User)' />
                </RadioGroup>
              </Grid.Item>
              <Grid.Item sm={12}>
                <Input label='Session Identifier' value={options.sessionIdentifier} onChange={handleApplePaylaterInputChange} name='sessionIdentifier' />
              </Grid.Item>
              <Grid.Item sm={12}>
                <Input label='Verifier' value={options.verifier} onChange={handleApplePaylaterInputChange} name='verifier' />
              </Grid.Item>
            </>
          )}
        </Grid>
        <Grid>
          <div>
            <Link size='S' onClick={handleRestart}>
              Restart Checkout
            </Link>
          </div>
        </Grid>
        <Grid>
          <p>Cookies</p>
          <pre style={cookiesStyle}>{cookies.map((c) => JSON.stringify(c) + '\n')}</pre>
        </Grid>
      </div>
    </div>
  )
}
